import { Flex } from '@core/ui-elements'
import { CardColors, cardColorSchemaList } from '@utils/styles'
import { FC, MouseEventHandler, ReactNode } from 'react'

export interface ICard {
  testId?: string
  secondaryArea?: ReactNode
  color?: CardColors
  borderStyle?: CardBorderStyles
  children: ReactNode
  onClick?: MouseEventHandler<HTMLDivElement>
  secondAreaBackgroundColor?: string
}

export enum CardBorderStyles {
  left = 'left',
  full = 'full',
}

export const cardColorSchema = {
  [CardColors.blueWhite]: {
    body: {
      [CardBorderStyles.left]: {
        backgroundColor: cardColorSchemaList[CardColors.blueWhite].secondary,
        borderLeft: '0.25rem solid',
        borderLeftColor: cardColorSchemaList[CardColors.blueWhite].primary,
      },
      [CardBorderStyles.full]: {
        backgroundColor: cardColorSchemaList[CardColors.blueWhite].secondary,
        border: '0.0625rem solid',
        borderColor: cardColorSchemaList[CardColors.blueWhite].primary,
      },
    },
    secondaryAreaColor: 'whiteAlpha.100',
  },
  [CardColors.blue]: {
    body: {
      [CardBorderStyles.left]: {
        backgroundColor: cardColorSchemaList[CardColors.blue].secondary,
        borderLeft: '0.25rem solid',
        borderLeftColor: cardColorSchemaList[CardColors.blue].primary,
      },
      [CardBorderStyles.full]: {
        backgroundColor: cardColorSchemaList[CardColors.blue].secondary,
        border: '0.0625rem solid',
        borderColor: cardColorSchemaList[CardColors.blue].primary,
      },
    },
    secondaryAreaColor: 'blue.50',
  },
  [CardColors.red]: {
    body: {
      [CardBorderStyles.left]: {
        backgroundColor: cardColorSchemaList[CardColors.red].secondary,
        borderLeft: '0.25rem solid',
        borderLeftColor: cardColorSchemaList[CardColors.red].primary,
      },
      [CardBorderStyles.full]: {
        backgroundColor: cardColorSchemaList[CardColors.red].secondary,
        border: '0.0625rem solid',
        borderColor: cardColorSchemaList[CardColors.red].primary,
      },
    },
    secondaryAreaColor: 'red.50',
  },
  [CardColors.yellow]: {
    body: {
      [CardBorderStyles.left]: {
        backgroundColor: cardColorSchemaList[CardColors.yellow].secondary,
        borderLeft: '0.25rem solid',
        borderLeftColor: cardColorSchemaList[CardColors.yellow].primary,
      },
      [CardBorderStyles.full]: {
        backgroundColor: cardColorSchemaList[CardColors.yellow].secondary,
        border: '0.0625rem solid',
        borderColor: cardColorSchemaList[CardColors.yellow].primary,
      },
    },
    secondaryAreaColor: 'orange.50',
  },
  [CardColors.grayWhite]: {
    body: {
      [CardBorderStyles.left]: {
        backgroundColor: cardColorSchemaList[CardColors.grayWhite].secondary,
        borderLeft: '0.25rem solid',
        borderLeftColor: cardColorSchemaList[CardColors.grayWhite].primary,
      },
      [CardBorderStyles.full]: {
        backgroundColor: cardColorSchemaList[CardColors.grayWhite].secondary,
        border: '0.0625rem solid',
        borderColor: cardColorSchemaList[CardColors.grayWhite].primary,
      },
    },
    secondaryAreaColor: 'gray.100',
  },
  [CardColors.gray]: {
    body: {
      [CardBorderStyles.left]: {
        backgroundColor: cardColorSchemaList[CardColors.gray].secondary,
        borderLeft: '0.25rem solid',
        borderLeftColor: cardColorSchemaList[CardColors.gray].primary,
      },
      [CardBorderStyles.full]: {
        backgroundColor: cardColorSchemaList[CardColors.gray].secondary,
        border: '0.0625rem solid',
        borderColor: cardColorSchemaList[CardColors.gray].primary,
      },
    },
    secondaryAreaColor: 'whiteAlpha.900',
  },
  [CardColors.green]: {
    body: {
      [CardBorderStyles.left]: {
        backgroundColor: cardColorSchemaList[CardColors.green].secondary,
        borderLeft: '0.25rem solid',
        borderLeftColor: cardColorSchemaList[CardColors.green].primary,
      },
      [CardBorderStyles.full]: {
        backgroundColor: cardColorSchemaList[CardColors.green].secondary,
        border: '0.0625rem solid',
        borderColor: cardColorSchemaList[CardColors.green].primary,
      },
    },
    secondaryAreaColor: 'whiteAlpha.900',
  },
  [CardColors.intense_green]: {
    body: {
      [CardBorderStyles.left]: {
        backgroundColor:
          cardColorSchemaList[CardColors.intense_green].secondary,
        borderLeft: '0.25rem solid',
        borderLeftColor: cardColorSchemaList[CardColors.intense_green].primary,
      },
      [CardBorderStyles.full]: {
        backgroundColor:
          cardColorSchemaList[CardColors.intense_green].secondary,
        border: '0.0625rem solid',
        borderColor: cardColorSchemaList[CardColors.intense_green].primary,
      },
    },
    secondaryAreaColor: 'whiteAlpha.900',
  },
}

export const Card: FC<ICard> = ({
  testId,
  secondaryArea,
  color = CardColors.grayWhite,
  borderStyle = CardBorderStyles.left,
  children,
  onClick,
  secondAreaBackgroundColor,
}) => {
  return (
    <Flex
      borderRadius={'0.5rem'}
      boxShadow={
        '0 1px 2px 0 rgba(0, 0, 0, 0.06), 0 1px 3px 0 rgba(0, 0, 0, 0.10)'
      }
      flexDirection={'column'}
      padding={'0'}
      width={'100%'}
      {...cardColorSchema[color].body[borderStyle]}
      cursor={onClick ? 'pointer' : undefined}
      data-testid={testId}
      onClick={onClick}
    >
      <Flex
        borderTopRadius={'0.5rem'}
        flexDirection={'column'}
        gap={'0.5rem'}
        padding={'0.5rem 0.5rem 0.5rem 0.75rem'}
      >
        {children}
      </Flex>

      {secondaryArea ? (
        <Flex
          backgroundColor={
            secondAreaBackgroundColor
              ? secondAreaBackgroundColor
              : cardColorSchema[color].secondaryAreaColor
          }
          borderBottomRadius={'0.5rem'}
          padding={'0.5rem 0.5rem 0.5rem 0.75rem'}
        >
          {secondaryArea}
        </Flex>
      ) : null}
    </Flex>
  )
}
